import React, { useCallback, useMemo } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, Select, SubmitButton, InputNumber } from "formik-antd";
import { criteriaSchema } from "/app/src/schemas";
import { useTranslation } from "react-i18next";
import { handleSubmissionErrors } from "/app/src/helpers/forms";
import { WorkflowCriteria, Workflow, Report } from "/app/src/models";

interface FormValues {
  reportId: number | undefined;
  verb: string | undefined;
  number: string | undefined;
}

/**
 * Formats the form values into a specific structure.
 *
 * @param {WorkflowCriteria} values - The form values to format.
 * @returns {Object} The formatted form values.
 */
function formatForm(values: WorkflowCriteria) {
  return {
    workflowId: values.workflowId,
    reportId: values.reportId,
    value: values.value,
    verb: values.verb,
  };
}

/**
 * Creates a new criteria component for a workflow.
 *
 * @param {object} param0 - The props object.
 * @param {Workflow} param0.workflow - The workflow details.
 * @param {function} param0.addWorkflowCriteria - Function to add workflow criteria.
 * @param {Report[]} param0.reports - Array of reports.
 * @returns {JSX.Element} The rendered new criteria component.
 */
export default function NewCriteria({
  workflow,
  addWorkflowCriteria,
  reports,
}: {
  workflow: Workflow;
  addWorkflowCriteria: (workflowCriteria: WorkflowCriteria) => Promise<any>;
  reports: Report[];
}) {
  const { t } = useTranslation();
  const verbs = useMemo(
    () => [
      { name: "equals", value: "eq" },
      { name: "not", value: "not" },
      { name: "greater than", value: "gt" },
      { name: "less than", value: "lt" },
      { name: "greater or equal", value: "ge" },
      { name: "less or equal", value: "le" },
    ],
    [],
  );

  /**
   * Creates new criteria based on form values and handles the form submission.
   *
   * @param {object} values - The form values.
   * @param {object} actions - The Formik actions.
   * @returns {void}
   */
  const createCriteria = useCallback(
    (values, actions) => {
      const newCriteria = {
        ...values,
        workflowId: workflow.id,
      } as WorkflowCriteria;
      addWorkflowCriteria(formatForm(newCriteria)).then((response) => {
        if (response?.errors) {
          handleSubmissionErrors(response.errors, actions.setFieldError);
        } else {
          actions.resetForm();
        }
      });
    },
    [workflow, addWorkflowCriteria],
  );

  /**
   * Renders the form for creating new criteria.
   *
   * @param {FormikProps<FormValues>} props - The Formik props.
   * @returns {JSX.Element} The form element.
   */
  const newCriteriaForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty }) => (
        <Form>
          <Row justify="start" align="middle" gutter={16}>
            <Col span={5}>
              <h4>{t("translation:report")}</h4>
              <Form.Item name="reportId">
                <Select
                  name="reportId"
                  size="large"
                  placeholder={t("translation:select_report")}
                >
                  {reports.map((c) => (
                    <Select.Option key={c.id} value={c.id}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={6}>
              <span className="blockText">{t("translation:row_count")}</span>
            </Col>
            <Col span={5}>
              <h4>{t("translation:comparison")}</h4>
              <Form.Item name="verb">
                <Select
                  name="verb"
                  size="large"
                  placeholder={t("translation:select_comparison")}
                >
                  {verbs.map((c) => (
                    <Select.Option key={c.value} value={c.value}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={5}>
              <h4>{t("translation:value")}</h4>
              <Form.Item name="value" hasFeedback={false}>
                <InputNumber name="value" min={0} max={1000000} size="large" />
              </Form.Item>
            </Col>
            <Col span={3}>
              <SubmitButton size="large" block disabled={!dirty}>
                {t("translation:add")}
              </SubmitButton>
            </Col>
          </Row>
        </Form>
      ),
      [t, reports, verbs],
    );
  const initialValues: FormValues = {
    reportId: undefined,
    verb: undefined,
    number: undefined,
  };
  return (
    <div className="newWorkflowCriteria">
      <h3>
        {t("translation:add")} {t("translation:criteria")}
      </h3>
      <Formik
        component={newCriteriaForm}
        initialValues={initialValues}
        validationSchema={criteriaSchema}
        onSubmit={createCriteria}
      />
    </div>
  );
}
